import { dbManager } from './db_manager';
import { notificationService } from './notificationService';
import { cacheManager } from './cacheManager';

const INTERACTION_CACHE_KEY = 'libritui_interactions_cache';
const MAX_CACHE_ITEMS = 5000;

const InteractionCache = {
    get() {
        try {
            const data = localStorage.getItem(INTERACTION_CACHE_KEY);
            return data ? JSON.parse(data) : {};
        } catch (e) {
            return {};
        }
    },
    set(data: any) {
        try {
            const keys = Object.keys(data);
            if (keys.length > MAX_CACHE_ITEMS) {
                const keysToDelete = keys.slice(0, keys.length - MAX_CACHE_ITEMS);
                keysToDelete.forEach(k => delete data[k]);
            }
            localStorage.setItem(INTERACTION_CACHE_KEY, JSON.stringify(data));
        } catch (e) {}
    },
    update(key: string, value: any) {
        const data = this.get();
        data[key] = value;
        this.set(data);
    }
};

export const socialService = {
    async _notifyMentions(actorId: string, content: string, targetType: 'post' | 'comment' | 'club_comment', targetId: string) {
        const mentions = [...content.matchAll(/\B@([a-zA-Z0-9_.-]+)/g)].map(m => m[1]);
        if (mentions.length === 0) return;
        for (const username of new Set(mentions)) {
            try {
                const result = await dbManager.execute({
                    action: 'select_public',
                    table: 'profiles',
                    filters: { username: username.toLowerCase() }
                });
                const profiles = Array.isArray(result) ? result : (result?.data || []);
                const match = profiles.find((p: any) => p.username.toLowerCase() === username.toLowerCase());
                if (match && match.id && match.id !== actorId) {
                    await notificationService.createNotification(match.id, actorId, 'mention', targetType, targetId);
                }
            } catch (e) { console.error("Mention notify error", e); }
        }
    },

    // Community Posts
    async getPosts(limit: number = 10, offset: number = 0, filters?: any) {
        // Deep Fetch in background will be handled by the UI, but we provide pagination here
        return dbManager.from('community_posts').select(filters, { limit, offset }, 'profiles');
    },

    async getUserPosts(userId: string, limit: number = 10, offset: number = 0) {
        return dbManager.from('community_posts').select({ user_id: userId }, { limit, offset }, 'profiles');
    },

    async getSavedPosts(userId: string) {
        // First get saved interactions from the new plural table
        const saved = await dbManager.from('posts_interactions', userId.toString()).select({ 
            user_id: userId.toString(), 
            type: 'save' 
        });
        
        if (!saved || saved.length === 0) return [];
        
        const postIds = saved.map((s: any) => s.post_id.toString());
        // Then fetch those posts (Publicly)
        return dbManager.execute({
            action: 'select_public',
            table: 'community_posts',
            filters: { IN_POST_IDS: postIds }
        });
    },

    async createPost(userId: string, postData: any) {
        const id = postData.id || crypto.randomUUID();
        const res = await dbManager.from('community_posts', userId).upsert({
            id,
            user_id: userId,
            ...postData,
            created_at: new Date().toISOString()
        });
        
        cacheManager.clear(`community_feed_${userId}_page_1`);
        
        if (postData.content) {
            this._notifyMentions(userId, postData.content, 'post', id).catch(e => console.error(e));
        }
        return res;
    },

    async deletePost(postId: string, userId: string) {
        cacheManager.clear(`community_feed_${userId}_page_1`);
        return dbManager.from('community_posts', userId).delete(postId);
    },

    // Post Interactions (Likes, Comments, Reposts, Saves) - HERO MODE
    async getPostInteractions(postId: string) {
        return dbManager.execute({
            action: 'select_public',
            table: 'posts_interactions',
            filters: { post_id: postId.toString() },
            join: 'posts_interactions'
        });
    },

    async getPostInteractionsForUser(userId: string) {
        const cacheKey = `user_post_interactions_${userId}`;
        const cached = InteractionCache.get()[cacheKey];
        
        // Background fetch to keep sync
        dbManager.from('posts_interactions', userId.toString()).select({ 
            user_id: userId.toString() 
        }).then(data => {
            InteractionCache.update(cacheKey, data);
        }).catch(e => console.error("Background sync for interactions failed", e));

        if (cached) return cached;
        
        const data = await dbManager.from('posts_interactions', userId.toString()).select({ 
            user_id: userId.toString() 
        });
        InteractionCache.update(cacheKey, data);
        return data;
    },

    async toggleInteraction(userId: string, postId: string, type: 'like' | 'save' | 'repost') {
        // Optimistic cache update
        const cacheKey = `user_post_interactions_${userId}`;
        const data = InteractionCache.get();
        let arr = data[cacheKey] || [];
        const existingIdx = arr.findIndex((i: any) => String(i.post_id) === String(postId) && i.type === type);
        
        if (existingIdx >= 0) {
            arr.splice(existingIdx, 1);
        } else {
            arr.push({ user_id: userId, post_id: postId, type });
        }
        InteractionCache.update(cacheKey, arr);

        // Clear SWR cache for immediate UI refresh
        cacheManager.clear(`community_feed_${userId}_page_1`);
        cacheManager.clear(`post_interactions_${postId}`);

        const result = await dbManager.execute({
            action: 'toggle_interaction',
            table: 'posts_interactions',
            data: { post_id: postId.toString(), type: type },
            user_id: userId.toString()
        });

        // Notify like
        if (result && result.state === true && type === 'like') {
            setTimeout(async () => {
                try {
                    const post = await dbManager.execute({ action: 'select_public', table: 'community_posts', filters: { id: postId.toString() }});
                    const ownerId = post && post.length > 0 ? post[0].user_id : null;
                    if (ownerId && ownerId !== userId) await notificationService.createNotification(ownerId, userId, 'like', 'post', postId);
                } catch(e) {}
            }, 100);
        }

        return result;
    },

    async addPostInteraction(userId: string, postId: string, type: 'like' | 'save' | 'repost') {
        // Fallback to toggle for atomic consistency
        return this.toggleInteraction(userId, postId, type);
    },

    async deletePostInteraction(userId: string, postId: string, type: string) {
        // Fallback to toggle for atomic consistency
        return this.toggleInteraction(userId, postId, type as any);
    },
    
    // Dedicated Post Comments - HERO MODE (Table: post_comments)
    getCachedComments(postId: string) {
        try {
            const cached = localStorage.getItem(`comments_cache_${postId}`);
            return cached ? JSON.parse(cached) : null;
        } catch { return null; }
    },

    async getPostComments(postId: string, currentUserId?: string, limit: number = 10, offset: number = 0) {
        const result = await dbManager.execute({
            action: 'select_public',
            table: 'post_comments',
            filters: { post_id: postId.toString() },
            join: 'profiles',
            data: { limit, offset }
        });

        const comments = Array.isArray(result) ? result : (result?.data || []);
        if (comments.length === 0) return comments;

        // Fetch Reactions for all these comments - Wrap in try-catch to handle missing table
        let reactionsMap = new Map<string, any[]>();
        try {
            const commentIds = comments.map((c: any) => c.id);
            const reactionsResult = await dbManager.execute({
                action: 'select_public',
                table: 'posts_comments_reactions',
                filters: { IN_COMMENT_IDS: commentIds }
            });

            const rawReactions = Array.isArray(reactionsResult) ? reactionsResult : (reactionsResult?.data || []);
            
            // Group reactions by commentId
            rawReactions.forEach((r: any) => {
                if (!reactionsMap.has(r.comment_id)) reactionsMap.set(r.comment_id, []);
                reactionsMap.get(r.comment_id)!.push(r);
            });
        } catch (e) {
            console.error("Error fetching comment reactions (table might be missing):", e);
        }

        // 3. Manually fetch profiles for comments to guarantee avatars and usernames work, 
        // in case the D1 worker doesn't support 'join: profiles' on post_comments.
        const userIds = [...new Set(comments.map((c: any) => c.user_id))].filter(Boolean);
        const profilesMap = new Map<string, any>();
        if (userIds.length > 0) {
            try {
                const profilesResult = await Promise.all(
                    userIds.map(uid => dbManager.execute({
                        action: 'select_public',
                        table: 'profiles',
                        filters: { id: uid.toString() }
                    }).then(res => {
                        return (res && res.data && res.data.length > 0) ? res.data[0] : (Array.isArray(res) && res.length > 0 ? res[0] : null);
                    }))
                );
                profilesResult.forEach(p => { if (p) profilesMap.set(p.id, p); });
            } catch (e) {
                console.error("Error manually fetching profiles for comments:", e);
            }
        }

        const processedComments = comments.map((c: any) => {
            const reacts = reactionsMap.get(c.id) || [];
            let formattedReactions: any[] = [];
            
            if (reacts.length > 0) {
                const emojiCounts = new Map<string, number>();
                const userHasReacted = new Set<string>();

                reacts.forEach(r => {
                    const count = emojiCounts.get(r.emoji) || 0;
                    emojiCounts.set(r.emoji, count + 1);
                    if (currentUserId && String(r.user_id) === String(currentUserId)) {
                        userHasReacted.add(r.emoji);
                    }
                });

                formattedReactions = Array.from(emojiCounts.entries()).map(([emoji, count]) => ({
                    emoji,
                    count,
                    reacted: userHasReacted.has(emoji)
                }));
            }

            return { 
                ...c, 
                reactions: formattedReactions,
                profiles: c.profiles || profilesMap.get(c.user_id) || null
            };
        });

        // Save to cache for SWR
        localStorage.setItem(`comments_cache_${postId}`, JSON.stringify(processedComments));

        return processedComments;
    },

    async addPostComment(userId: string, postId: string, content: string, parentId: string | null = null) {
        const id = crypto.randomUUID();
        const res = await dbManager.from('post_comments', userId.toString()).insert({
            id,
            post_id: postId.toString(),
            user_id: userId.toString(),
            content,
            parent_id: parentId ? parentId.toString() : null,
            reactions: '[]',
            created_at: new Date().toISOString()
        });

        // Invalidate community feed and comments cache
        cacheManager.clear(`community_feed_${userId}_page_1`);
        cacheManager.clear(`community_feed_comments_${postId}`);

        this._notifyMentions(userId, content, 'comment', postId).catch(e => console.error(e));

        // Notify post owner or comment owner
        setTimeout(async () => {
            try {
                if (parentId) {
                    const parent = await dbManager.execute({ action: 'select_public', table: 'post_comments', filters: { id: parentId }});
                    const ownerId = parent && parent.length > 0 ? parent[0].user_id : null;
                    if (ownerId && ownerId !== userId) await notificationService.createNotification(ownerId, userId, 'reply', 'comment', postId);
                } else {
                    const post = await dbManager.execute({ action: 'select_public', table: 'community_posts', filters: { id: postId }});
                    const ownerId = post && post.length > 0 ? post[0].user_id : null;
                    if (ownerId && ownerId !== userId) await notificationService.createNotification(ownerId, userId, 'comment', 'post', postId);
                }
            } catch(e) {}
        }, 100);

        return res;
    },

    async deletePostComment(commentId: string, userId: string) {
        return dbManager.from('post_comments', userId.toString()).delete(commentId.toString());
    },

    // Cascade-delete all comments belonging to a post (called by post owner before deleting the post).
    async deleteAllPostComments(userId: string, postId: string) {
        return dbManager.execute({
            action: 'delete_filtered',
            table: 'post_comments',
            filters: { post_id: postId.toString() },
            user_id: userId.toString(),
        });
    },

    // Saved Books (Social context)
    async saveSocialBook(userId: string, data: any) {
        return dbManager.from('saved_books', userId).upsert({
            user_id: userId,
            ...data,
            saved_at: new Date().toISOString()
        });
    },

    async unsaveSocialBook(userId: string, bookId: string) {
        return dbManager.execute({
            action: 'delete_filtered',
            table: 'saved_books',
            filters: { book_id: bookId },
            user_id: userId
        });
    },

    // Book Clubs
    async getClubs(userId: string) {
        const createdResults = await dbManager.from('book_clubs', userId).select({ creator_id: userId }) || [];
        const membershipsResults = await dbManager.from('book_club_members', userId).select({ user_id: userId }) || [];

        const clubIds = membershipsResults.map((m: any) => m.club_id);
        let memberClubs = [];

        if (clubIds.length > 0) {
            const clubsData = await dbManager.execute({
                action: 'select_public',
                table: 'book_clubs',
                filters: { IN_CLUB_IDS: clubIds }
            });
            const validClubs = Array.isArray(clubsData) ? clubsData : (clubsData?.data || []);
            
            memberClubs = membershipsResults.map((m: any) => ({
                ...m,
                book_clubs: validClubs.find((c: any) => c.id === m.club_id)
            })).filter((m: any) => m.book_clubs);
        }

        return {
            created: createdResults,
            memberships: memberClubs
        };
    },

    async getClubById(clubId: string) {
        const results = await dbManager.execute({
            action: 'select_public',
            table: 'book_clubs',
            filters: { id: clubId }
        });
        return results && results.length > 0 ? results[0] : null;
    },

    async createClub(userId: string, clubData: any) {
        const clubId = `club_${Date.now()}`;
        const now = new Date().toISOString();
        await dbManager.from('book_clubs', userId).upsert({
            id: clubId,
            creator_id: userId,
            status: 'ativo',
            ...clubData,
            created_at: now,
            updated_at: now
        });

        // Auto-add creator as permanent member
        await dbManager.from('book_club_members', userId).upsert({
            club_id: clubId,
            user_id: userId,
            invited_by: userId,
            status: 'accepted',
            joined_at: now
        });
        return clubId;
    },

    // Club Members
    async getClubMembers(clubId: string) {
        const response = await dbManager.execute({
            action: 'select_public',
            table: 'book_club_members',
            filters: { club_id: clubId },
            join: 'book_club_members'
        });
        const members = Array.isArray(response) ? response : (response?.data || []);
        return members.map((m: any) => ({
            ...m,
            profile: m.profiles || m.profile || null
        }));
    },

    async inviteMember(clubId: string, userId: string, invitedBy: string) {
        return dbManager.from('book_club_members', invitedBy).upsert({
            club_id: clubId,
            user_id: userId,
            invited_by: invitedBy,
            status: 'pending',
            joined_at: new Date().toISOString()
        });
    },

    async updateMemberStatus(clubId: string, userId: string, status: string, invitedBy: string, shelfBookId: string | null = null) {
        return dbManager.from('book_club_members', userId).upsert({
            club_id: clubId,
            user_id: userId,
            invited_by: invitedBy,
            status,
            joined_at: status === 'accepted' ? new Date().toISOString() : null,
            shelf_book_id: shelfBookId
        });
    },

    // Club Comments
    async getComments(clubId: string, currentUserId?: string) {
        const result = await dbManager.execute({
            action: 'select_public',
            table: 'club_comments',
            filters: { club_id: clubId },
            join: 'club_comments'
        });

        const comments = Array.isArray(result) ? result : (result?.data || []);
        if (comments.length === 0) return comments;

        // Fetch Reactions for all these comments to compose the UI
        const commentIds = comments.map((c: any) => c.id);
        const reactionsResult = await dbManager.execute({
            action: 'select_public',
            table: 'club_comment_reactions',
            filters: { IN_COMMENT_IDS: commentIds }
        });

        const rawReactions = Array.isArray(reactionsResult) ? reactionsResult : (reactionsResult?.data || []);
        
        // Group reactions by commentId
        const reactionsMap = new Map<string, any[]>();
        rawReactions.forEach((r: any) => {
            if (!reactionsMap.has(r.comment_id)) reactionsMap.set(r.comment_id, []);
            reactionsMap.get(r.comment_id)!.push(r);
        });

        // Map them into the expected format
        return comments.map((c: any) => {
            const reacts = reactionsMap.get(c.id) || [];
            if (reacts.length === 0) return { ...c, reactions: [] };

            const emojiCounts = new Map<string, number>();
            const userHasReacted = new Set<string>();

            reacts.forEach(r => {
                const count = emojiCounts.get(r.emoji) || 0;
                emojiCounts.set(r.emoji, count + 1);
                if (currentUserId && r.user_id === currentUserId) {
                    userHasReacted.add(r.emoji);
                }
            });

            const formattedReactions = Array.from(emojiCounts.entries()).map(([emoji, count]) => ({
                emoji,
                count,
                reacted: userHasReacted.has(emoji)
            }));

            return { ...c, reactions: formattedReactions };
        });
    },

    async addComment(userId: string, clubId: string, content: string, parentId: string | null = null, isSpoiler: boolean = false, customId?: string) {
        const id = customId || `comm_${Date.now()}`;
        const now = new Date().toISOString();
        const res = await dbManager.from('club_comments', userId).upsert({
            id,
            user_id: userId,
            club_id: clubId,
            content,
            parent_id: parentId,
            is_spoiler: isSpoiler ? 1 : 0,
            created_at: now,
            updated_at: now
        });

        this._notifyMentions(userId, content, 'club_comment', clubId).catch(e => console.error(e));

        setTimeout(async () => {
            try {
                if (parentId) {
                    const parent = await dbManager.execute({ action: 'select_public', table: 'club_comments', filters: { id: parentId }});
                    const ownerId = parent && parent.length > 0 ? parent[0].user_id : null;
                    if (ownerId && ownerId !== userId) await notificationService.createNotification(ownerId, userId, 'reply', 'club_comment', clubId);
                }
            } catch(e) {}
        }, 100);

        return res;
    },

    async deleteComment(commentId: string, userId: string) {
        return dbManager.from('club_comments', userId).delete(commentId);
    },

    // Reactions
    async getReactions(commentIds: string[]) {
        // If few commentIds, we can fetch all or filtered
        const results = await dbManager.execute({
            action: 'select_public',
            table: 'club_comment_reactions'
        });
        return results || [];
    },

    async toggleReaction(userId: string, commentId: string, emoji: string) {
        return this._toggleAnyReaction(userId, commentId, emoji, 'club_comment_reactions');
    },

    async togglePostCommentReaction(userId: string, commentId: string, emoji: string) {
        return this._toggleAnyReaction(userId, commentId, emoji, 'posts_comments_reactions');
    },

    async _toggleAnyReaction(userId: string, commentId: string, emoji: string, table: string) {
        // Optimistic update of local comments cache to persist reaction instantly
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('comments_cache_')) {
                    const cachedData = localStorage.getItem(key);
                    if (cachedData) {
                        const comments = JSON.parse(cachedData);
                        const commentIdx = comments.findIndex((c: any) => String(c.id) === String(commentId));
                        if (commentIdx >= 0) {
                            let reactions = comments[commentIdx].reactions || [];
                            const rIdx = reactions.findIndex((r: any) => r.emoji === emoji);
                            if (rIdx >= 0) {
                                reactions[rIdx].reacted = !reactions[rIdx].reacted;
                                reactions[rIdx].count += reactions[rIdx].reacted ? 1 : -1;
                            } else {
                                reactions.push({ emoji, count: 1, reacted: true });
                            }
                            comments[commentIdx].reactions = reactions;
                            localStorage.setItem(key, JSON.stringify(comments));
                            break;
                        }
                    }
                }
            }
        } catch (e) {}

        // Find ANY existing reaction by this user on this comment
        const existing = await dbManager.execute({
            action: 'select_public',
            table,
            filters: { comment_id: commentId.toString(), user_id: userId.toString() }
        });

        const existingReaction = (existing && existing.length > 0) ? existing[0] : null;

        if (existingReaction) {
            // Delete the old one regardless (we either toggle off or swap)
            await dbManager.execute({
                action: 'delete_filtered',
                table,
                filters: { comment_id: commentId.toString(), user_id: userId.toString(), emoji: existingReaction.emoji }
            });

            // If it was a DIFFERENT emoji, insert the new one (Swap)
            if (existingReaction.emoji !== emoji) {
                const now = new Date().toISOString();
                return dbManager.execute({
                    action: 'insert',
                    table,
                    data: {
                        id: `react_${Date.now()}_${userId}`,
                        user_id: userId.toString(),
                        comment_id: commentId.toString(),
                        emoji,
                        created_at: now,
                        updated_at: now
                    }
                });
            }
            // If it was the SAME emoji, it's already deleted (Toggle OFF)
            return { success: true, toggledOff: true };
        } else {
            // No previous reaction, insert new (Toggle ON)
            const now = new Date().toISOString();
            return dbManager.execute({
                action: 'insert',
                table,
                data: {
                    id: `react_${Date.now()}_${userId}`,
                    user_id: userId.toString(),
                    comment_id: commentId.toString(),
                    emoji,
                    created_at: now,
                    updated_at: now
                }
            });
        }
    }
};
